"use client";

import { useTranslations } from "next-intl";
import {
    AppButton,
    AppInput,
    AppInputPhone,
    AppSelect,
    AppSelectOption,
} from "@/components/design/forms";
import { ContactInput } from "@/components/design/shared/ContactInput";
import { CountrySearchInput } from "@/components/design/shared/CountrySearchInput";
import type { ParticipantFormData } from "./registrationFlowTypes";
import {
    LEVEL_SELECT_OPTIONS,
    ROLE_SELECT_OPTIONS,
} from "@/lib/participantFieldOptions";
import {
    isSalutationMrSelection,
    isSalutationMsSelection,
} from "@/lib/participantGenderSalutation";

export interface ParticipantChampionshipPartner1SectionProps {
    participant: ParticipantFormData;
    index: number;
    errors: Record<string, string>;
    onUpdate: (data: Partial<ParticipantFormData>) => void;
    /** Телефон необязателен, если карточка не помечена как primary. */
    phoneOptional?: boolean;
}

export function ParticipantChampionshipPartner1Section({
    participant,
    index,
    errors,
    onUpdate,
    phoneOptional = false,
}: ParticipantChampionshipPartner1SectionProps) {
    const t = useTranslations("strings.registrationFlow");
    const tf = useTranslations("strings.participantFieldOptions");
    return (
        <div className="min-w-0 max-w-full rounded-xl bg-fest-canvas p-3 sm:p-4">
                            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex min-w-0 flex-col gap-1">
                                    <h4 className="text-sm font-bold uppercase tracking-wider text-fest-gold">
                                        {t("partner1Section")}
                                    </h4>
                                    {!participant.isSolo && (
                                        <p className="text-xs text-fest-subtle-text">
                                            {t("partner1PairHint")}
                                        </p>
                                    )}
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    {/* Solo Button */}
                                    <AppButton
                                        type="button"
                                        onClick={() =>
                                            onUpdate({
                                                isSolo: !participant.isSolo,
                                                lookingForPartner: null,
                                            })
                                        }
                                        className={`px-3 py-1.5 text-xs font-semibold border-2 rounded-lg transition-all ${
                                            participant.isSolo
                                                ? "border-fest-gold bg-fest-gold text-white shadow-md"
                                                : "border-fest-border-strong bg-white text-fest-heading hover:border-fest-gold hover:shadow"
                                        }`}
                                    >
                                        {t("soloLabel")}
                                    </AppButton>
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                {/* Row 1: Gender Selection + Age (mobile) / Gender + First Name + Last Name + Level + Age (desktop) */}
                                <div className="grid min-w-0 w-full max-w-full grid-cols-12 gap-1.5">
                                    {/* Gender Selection */}
                                    <div className="col-span-6 md:col-span-2">
                                        <label className="block text-xs font-medium text-fest-heading mb-0.5">
                                            {t("genderLabel")}
                                        </label>
                                        <div className="flex min-h-[30px] flex-wrap items-center gap-x-2 gap-y-1">
                                            <label className="flex items-center gap-1 cursor-pointer group">
                                                <div className="relative">
                                                    <AppInput
                                                        type="radio"
                                                        name={`gender-${index}`}
                                                        checked={isSalutationMrSelection(
                                                            participant.gender,
                                                        )}
                                                        onChange={() =>
                                                            onUpdate({
                                                                gender: "mr",
                                                            })
                                                        }
                                                        className="sr-only"
                                                    />
                                                    <div
                                                        className={`w-3.5 h-3.5 rounded-full border-2 transition-all ${
                                                            isSalutationMrSelection(
                                                                participant.gender,
                                                            )
                                                                ? "border-fest-gold bg-fest-gold"
                                                                : "border-fest-border-strong bg-white group-hover:border-fest-gold"
                                                        }`}
                                                    >
                                                        {isSalutationMrSelection(
                                                            participant.gender,
                                                        ) && (
                                                            <div className="absolute inset-0 flex items-center justify-center">
                                                                <div className="w-1 h-1 rounded-full bg-white"></div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <span
                                                    className={`text-xs font-medium transition-colors ${
                                                        isSalutationMrSelection(
                                                            participant.gender,
                                                        )
                                                            ? "text-fest-heading"
                                                            : "text-fest-subtle-text group-hover:text-fest-heading"
                                                    }`}
                                                >
                                                    {t("salutationMr")}
                                                </span>
                                            </label>
                                            <label className="flex items-center gap-1 cursor-pointer group">
                                                <div className="relative">
                                                    <AppInput
                                                        type="radio"
                                                        name={`gender-${index}`}
                                                        checked={isSalutationMsSelection(
                                                            participant.gender,
                                                        )}
                                                        onChange={() =>
                                                            onUpdate({
                                                                gender: "ms",
                                                            })
                                                        }
                                                        className="sr-only"
                                                    />
                                                    <div
                                                        className={`w-3.5 h-3.5 rounded-full border-2 transition-all ${
                                                            isSalutationMsSelection(
                                                                participant.gender,
                                                            )
                                                                ? "border-fest-gold bg-fest-gold"
                                                                : "border-fest-border-strong bg-white group-hover:border-fest-gold"
                                                        }`}
                                                    >
                                                        {isSalutationMsSelection(
                                                            participant.gender,
                                                        ) && (
                                                            <div className="absolute inset-0 flex items-center justify-center">
                                                                <div className="w-1 h-1 rounded-full bg-white"></div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <span
                                                    className={`text-xs font-medium transition-colors ${
                                                        isSalutationMsSelection(
                                                            participant.gender,
                                                        )
                                                            ? "text-fest-heading"
                                                            : "text-fest-subtle-text group-hover:text-fest-heading"
                                                    }`}
                                                >
                                                    {t("salutationMs")}
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Age - visible on mobile in same row as gender */}
                                    <div className="col-span-6 md:col-span-1 md:order-6">
                                        <label className="block text-xs font-medium text-fest-heading mb-0.5">
                                            {t("ageLabel")}
                                        </label>
                                        <AppSelect
                                            value={participant.age}
                                            onChange={(e) =>
                                                onUpdate({
                                                    age: e.target.value,
                                                })
                                            }
                                            className="w-full px-1 py-1.5 text-sm bg-white border border-fest-border-strong rounded-lg text-fest-heading focus:outline-none focus:border-fest-gold transition-all appearance-none cursor-pointer"
                                        >
                                            <AppSelectOption value="">-</AppSelectOption>
                                            {Array.from(
                                                { length: 83 },
                                                (_, i) => 18 + i,
                                            ).map((age) => (
                                                <AppSelectOption
                                                    key={age}
                                                    value={age.toString()}
                                                >
                                                    {age}
                                                </AppSelectOption>
                                            ))}
                                        </AppSelect>
                                    </div>

                                    {/* First Name */}
                                    <div className="col-span-12 md:col-span-2 md:order-2">
                                        <label className="block text-xs font-medium text-fest-heading mb-0.5">
                                            {t("firstNameLabel")}
                                        </label>
                                        <AppInput
                                            type="text"
                                            value={participant.firstName}
                                            onChange={(e) =>
                                                onUpdate({
                                                    firstName: e.target.value,
                                                })
                                            }
                                            className={`w-full px-2.5 py-1.5 text-sm bg-white border rounded-lg text-fest-heading placeholder-fest-subtle-text focus:outline-none transition-all ${
                                                errors.firstName
                                                    ? "border-fest-red focus:border-fest-red"
                                                    : "border-fest-border-strong focus:border-fest-gold"
                                            }`}
                                            placeholder={t("placeholderFirstNameExample")}
                                        />
                                        {errors.firstName && (
                                            <p className="text-xs text-fest-red mt-0.5">
                                                {errors.firstName}
                                            </p>
                                        )}
                                    </div>

                                    {/* Last Name */}
                                    <div className="col-span-12 md:col-span-2 md:order-3">
                                        <label className="block text-xs font-medium text-fest-heading mb-0.5">
                                            {t("lastNameLabel")}
                                        </label>
                                        <AppInput
                                            type="text"
                                            value={participant.lastName}
                                            onChange={(e) =>
                                                onUpdate({
                                                    lastName: e.target.value,
                                                })
                                            }
                                            className={`w-full px-2.5 py-1.5 text-sm bg-white border rounded-lg text-fest-heading placeholder-fest-subtle-text focus:outline-none transition-all ${
                                                errors.lastName
                                                    ? "border-fest-red focus:border-fest-red"
                                                    : "border-fest-border-strong focus:border-fest-gold"
                                            }`}
                                            placeholder={t("placeholderLastNameExample")}
                                        />
                                        {errors.lastName && (
                                            <p className="text-xs text-fest-red mt-0.5">
                                                {errors.lastName}
                                            </p>
                                        )}
                                    </div>

                                    {/* Level */}
                                    <div className="col-span-12 md:col-span-2 md:order-4">
                                        <label className="block text-xs font-medium text-fest-heading mb-0.5">
                                            {t("levelLabel")}
                                        </label>
                                        <AppSelect
                                            value={participant.level}
                                            onChange={(e) =>
                                                onUpdate({
                                                    level: e.target
                                                        .value as any,
                                                })
                                            }
                                            className={`w-full px-2.5 py-1.5 text-sm bg-white border rounded-lg focus:outline-none transition-all appearance-none cursor-pointer border-fest-border-strong focus:border-fest-gold ${
                                                !participant.level
                                                    ? "text-fest-subtle-text"
                                                    : "text-fest-heading"
                                            }`}
                                        >
                                            <AppSelectOption value="">{t("selectPlaceholder")}</AppSelectOption>
                                            {LEVEL_SELECT_OPTIONS.map((opt) => (
                                                <AppSelectOption key={opt.value} value={opt.value}>
                                                    {tf(opt.labelKey)}
                                                </AppSelectOption>
                                            ))}
                                        </AppSelect>
                                    </div>

                                    {/* Role */}
                                    <div className="col-span-12 md:col-span-3 md:order-5">
                                        <label className="block text-xs font-medium text-fest-heading mb-0.5">
                                            {t("tangoRoleLabel")}
                                        </label>
                                        <AppSelect
                                            value={participant.role}
                                            onChange={(e) =>
                                                onUpdate({
                                                    role: e.target
                                                        .value as any,
                                                })
                                            }
                                            className={`w-full px-2.5 py-1.5 text-sm bg-white border rounded-lg focus:outline-none transition-all appearance-none cursor-pointer border-fest-border-strong focus:border-fest-gold ${
                                                !participant.role
                                                    ? "text-fest-subtle-text"
                                                    : "text-fest-heading"
                                            }`}
                                        >
                                            <AppSelectOption value="">{t("selectPlaceholder")}</AppSelectOption>
                                            {ROLE_SELECT_OPTIONS.map((opt) => (
                                                <AppSelectOption key={opt.value} value={opt.value}>
                                                    {tf(opt.labelKey)}
                                                </AppSelectOption>
                                            ))}
                                        </AppSelect>
                                    </div>
                                </div>

                                {/* Email, phone, country, city, contact — one row */}
                                <div className="grid min-w-0 w-full max-w-full grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-1.5 items-start">
                                    <div className="lg:col-span-2">
                                        <label className="block text-xs font-medium text-fest-heading mb-0.5">
                                            {t("emailLabel")}
                                        </label>
                                        <AppInput
                                            type="email"
                                            value={participant.email}
                                            onChange={(e) =>
                                                onUpdate({
                                                    email: e.target.value,
                                                })
                                            }
                                            className={`w-full px-2.5 py-1.5 text-sm bg-white border rounded-lg text-fest-heading placeholder-fest-subtle-text focus:outline-none transition-all ${
                                                errors.email
                                                    ? "border-fest-red focus:border-fest-red"
                                                    : "border-fest-border-strong focus:border-fest-gold"
                                            }`}
                                            placeholder={t("placeholderEmailExample")}
                                        />
                                        {errors.email && (
                                            <p className="text-xs text-fest-red mt-0.5">
                                                {errors.email}
                                            </p>
                                        )}
                                    </div>
                                    <div className="lg:col-span-2">
                                        <label className="block text-xs font-medium text-fest-heading mb-0.5">
                                            {t("phoneLabel")}
                                            {!phoneOptional ? (
                                                <span className="text-fest-red"> *</span>
                                            ) : null}
                                        </label>
                                        <AppInputPhone
                                            value={participant.phone}
                                            country={participant.country}
                                            allowEmpty={phoneOptional}
                                            onChange={(phone) => onUpdate({ phone })}
                                            className={`w-full px-2.5 py-1.5 text-sm bg-white border rounded-lg text-fest-heading placeholder-fest-subtle-text focus:outline-none transition-all ${
                                                errors.phone
                                                    ? "border-fest-red focus:border-fest-red"
                                                    : "border-fest-border-strong focus:border-fest-gold"
                                            }`}
                                            placeholder={t("placeholderPhoneExample")}
                                            ariaInvalid={!!errors.phone}
                                        />
                                        {errors.phone && (
                                            <p className="text-xs text-fest-red mt-0.5">
                                                {errors.phone}
                                            </p>
                                        )}
                                    </div>
                                    <div className="lg:col-span-2">
                                        <label className="block text-xs font-medium text-fest-heading mb-0.5">
                                            {t("countryLabel")}
                                        </label>
                                        <CountrySearchInput
                                            value={participant.country}
                                            onChange={(country) => onUpdate({ country })}
                                            error={!!errors.country}
                                            inputClassName={`w-full px-2.5 py-1.5 text-sm bg-white border rounded-lg text-fest-heading placeholder-fest-subtle-text focus:outline-none transition-all ${
                                                errors.country
                                                    ? "border-fest-red focus:border-fest-red"
                                                    : "border-fest-border-strong focus:border-fest-gold"
                                            }`}
                                            placeholder={t("countryPlaceholderAz")}
                                        />
                                        {errors.country && (
                                            <p className="text-xs text-fest-red mt-0.5">
                                                {errors.country}
                                            </p>
                                        )}
                                    </div>
                                    <div className="lg:col-span-2">
                                        <label className="block text-xs font-medium text-fest-heading mb-0.5">
                                            {t("cityLabel")}
                                        </label>
                                        <AppInput
                                            type="text"
                                            value={participant.city}
                                            onChange={(e) =>
                                                onUpdate({
                                                    city: e.target.value,
                                                })
                                            }
                                            className={`w-full px-2.5 py-1.5 text-sm bg-white border rounded-lg text-fest-heading placeholder-fest-subtle-text focus:outline-none transition-all ${
                                                errors.city
                                                    ? "border-fest-red focus:border-fest-red"
                                                    : "border-fest-border-strong focus:border-fest-gold"
                                            }`}
                                            placeholder={t("cityPlaceholderBaku")}
                                        />
                                        {errors.city && (
                                            <p className="text-xs text-fest-red mt-0.5">
                                                {errors.city}
                                            </p>
                                        )}
                                    </div>
                                    <div className="col-span-1 min-w-0 max-w-full sm:col-span-1 lg:col-span-4 justify-self-start w-full max-w-[min(100%,22rem)] lg:max-w-none">
                                        <label className="block text-xs font-medium text-fest-heading mb-0.5">
                                            {t("contactLabel")}
                                        </label>
                                        <ContactInput
                                            type={
                                                participant.contactType ||
                                                "telegram"
                                            }
                                            value={participant.contactValue}
                                            onTypeChange={(t) =>
                                                onUpdate({
                                                    contactType: t,
                                                    instagram:
                                                        t === "instagram"
                                                            ? participant.contactValue
                                                            : "",
                                                })
                                            }
                                            onValueChange={(v) =>
                                                onUpdate({
                                                    contactValue: v,
                                                    instagram:
                                                        participant.contactType ===
                                                        "instagram"
                                                            ? v
                                                            : participant.instagram,
                                                })
                                            }
                                            compact
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
    );
}
